import React from 'react';
import { useTranslation } from 'react-i18next';

interface ReplyReadOnlyBlockProps {
  answer: string;
  answeredAt?: string | null;
  answeredBy?: string | null;
  /** Display name of the admin who posted the reply (resolved server-side). */
  answeredByName?: string | null;
}

/**
 * Read-only block shown in place of the reply form when an inquiry already
 * has a non-empty answer. Replies are immutable in this spec (Requirement
 * 14.1) so the block has no edit or delete controls.
 */
const ReplyReadOnlyBlock: React.FC<ReplyReadOnlyBlockProps> = ({
  answer,
  answeredAt,
  answeredBy,
  answeredByName,
}) => {
  const { t } = useTranslation('admin');

  const formattedAnsweredAt = answeredAt ? answeredAt.slice(0, 19).replace('T', ' ') : '-';
  // Prefer the resolved display name; fall back to the raw Cognito sub only
  // when the name lookup missed (e.g. the replying admin was later deleted).
  const answererLabel = answeredByName && answeredByName.trim().length > 0
    ? answeredByName
    : answeredBy;

  return (
    <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50/60 p-4">
      <h4 className="text-sm font-semibold text-blue-900 mb-2">
        {t('inquiries.replyBlock.heading')}
      </h4>
      <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{answer}</p>

      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
        <span>
          <span className="font-medium text-gray-600 mr-1">
            {t('inquiries.replyBlock.answeredAt')}:
          </span>
          {formattedAnsweredAt}
        </span>
        {answererLabel && (
          <span>
            <span className="font-medium text-gray-600 mr-1">
              {t('inquiries.replyBlock.answeredBy')}:
            </span>
            {answererLabel}
          </span>
        )}
      </div>
    </div>
  );
};

export default ReplyReadOnlyBlock;
